/**
 * DuplicateDetectionCard — Componente autónomo extraído de SettingsPage.
 * Se renderiza en TeamConfigPage (origen) y se queda referenciado desde Settings.
 *
 * Endpoints:
 *  GET  /api/settings           → duplicate_detection_enabled
 *  PATCH /api/settings          → toggle on/off
 *  GET  /api/roulette/config    → dedup_scope, dedup_window_days, dedup_on_duplicate
 *  PUT  /api/roulette/config    → guardar dedup params
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Save } from 'lucide-react';

export default function DuplicateDetectionCard({ allowedModules = {} }) {
  const [enabled, setEnabled] = useState(false);
  const [cfg, setCfg] = useState({ dedup_scope: 'company', dedup_window_days: 0, dedup_on_duplicate: 'keep_owner' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        api.get('/settings').catch(() => ({ data: {} })),
        api.get('/roulette/config').catch(() => ({ data: {} })),
      ]);
      setEnabled(!!sRes.data?.duplicate_detection_enabled);
      setCfg({
        dedup_scope: rRes.data?.dedup_scope || 'company',
        dedup_window_days: Number(rRes.data?.dedup_window_days || 0),
        dedup_on_duplicate: rRes.data?.dedup_on_duplicate || 'keep_owner',
      });
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await api.put('/settings', { duplicate_detection_enabled: next });
    } catch (e) {
      setEnabled(!next);
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/roulette/config', cfg);
      toast.success('Configuración guardada');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo guardar';
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card/50 border-white/5" data-testid="duplicate-detection-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" /> Detección de Leads Duplicados
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Cuando está activo, los leads con el mismo email o teléfono se detectan como duplicados según el alcance que configures. La detección es global para todo el equipo de la licencia.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
          <div>
            <p className="text-sm text-white">Activar detección de duplicados</p>
            <p className="text-[10px] text-muted-foreground">Evita leads duplicados en tu pipeline</p>
          </div>
          <button
            onClick={toggle}
            className={`w-11 h-6 rounded-full transition-all relative ${enabled ? 'bg-amber-500' : 'bg-white/10'}`}
            data-testid="duplicate-detection-toggle"
          >
            <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${enabled ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>

        {enabled && (
          <div className="space-y-4 pt-2 border-t border-white/5">
            {(allowedModules.brands || allowedModules.products) && (
              <div className="space-y-1">
                <Label className="text-xs text-white/60">Alcance de duplicidad</Label>
                <Select value={cfg.dedup_scope} onValueChange={v => setCfg({ ...cfg, dedup_scope: v })}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="dedup-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Empresa completa — un teléfono, un lead global</SelectItem>
                    {allowedModules.brands && <SelectItem value="brand">Por Marca — mismo teléfono en distintas marcas</SelectItem>}
                    {allowedModules.products && <SelectItem value="product">Por Producto — mismo teléfono en distintos productos</SelectItem>}
                    {allowedModules.products && <SelectItem value="lead_source">Por Fuente — mismo teléfono en distintas fuentes</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-[9px] text-muted-foreground">
                  {cfg.dedup_scope === 'company' && 'Un teléfono solo puede existir una vez en toda la empresa.'}
                  {cfg.dedup_scope === 'brand' && 'El mismo número puede ser lead en distintas marcas.'}
                  {cfg.dedup_scope === 'product' && 'El mismo número puede ser lead en distintos productos. Ideal para escuelas con varios niveles.'}
                  {cfg.dedup_scope === 'lead_source' && 'El mismo número puede ser lead desde distintas fuentes. Máxima granularidad.'}
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-white/60">Ventana de tiempo (días)</Label>
              <Input
                type="number" min="0"
                value={cfg.dedup_window_days}
                onChange={e => setCfg({ ...cfg, dedup_window_days: Math.max(0, parseInt(e.target.value) || 0) })}
                className="bg-black/20 border-white/10 text-xs"
                placeholder="0"
                data-testid="dedup-window-input"
              />
              <p className="text-[9px] text-muted-foreground">
                {cfg.dedup_window_days === 0
                  ? '0 = sin límite. Un lead de cualquier fecha bloquea al nuevo.'
                  : `Solo se considera duplicado si el lead existente entró en los últimos ${cfg.dedup_window_days} días.`}
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-white/60">Acción en duplicado</Label>
              <Select value={cfg.dedup_on_duplicate} onValueChange={v => setCfg({ ...cfg, dedup_on_duplicate: v })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="dedup-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep_owner">Conservar asignación — el lead nuevo va al mismo vendedor que el anterior</SelectItem>
                  <SelectItem value="reassign">Reasignar — ignorar duplicado y asignar normalmente por ruleta</SelectItem>
                  <SelectItem value="create_new">Crear nuevo — ignorar duplicidad completamente</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[9px] text-muted-foreground">
                Si el vendedor original ya no está activo, se asigna a uno nuevo y se guarda el historial de consultas (parpadeo amarillo en Leads, resucitado rojo si estaba perdido).
              </p>
            </div>

            <Button onClick={save} disabled={saving} className="w-full bg-amber-500 hover:bg-amber-400 text-black text-xs h-9" data-testid="duplicate-detection-save">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
